import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { MessageCircle, User, Car, Phone, Mail, Calendar, ChevronRight } from 'lucide-react';
import { chatbotService } from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const ChatConversationsManager = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await chatbotService.getAllConversations();
      setConversations(response.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load conversations',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const viewConversation = (conversation) => {
    setSelectedConversation(conversation);
    setIsDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        <p className="mt-4 text-gray-600">Loading conversations...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            AI Chatbot Conversations
          </CardTitle>
          <p className="text-sm text-gray-600 mt-1">
            Total Conversations: {conversations.length}
          </p>
        </CardHeader>
        <CardContent>
          {conversations.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No conversations yet</p>
          ) : (
            <div className="space-y-4">
              {conversations.map((conv) => (
                <Card
                  key={conv.id}
                  className="hover:shadow-lg transition-all cursor-pointer"
                  onClick={() => viewConversation(conv)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                          {conv.customer_name && (
                            <div className="flex items-center gap-1 text-sm">
                              <User className="w-4 h-4 text-blue-600" />
                              <span className="font-medium">{conv.customer_name}</span>
                            </div>
                          )}
                          {conv.customer_mobile && (
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <Phone className="w-4 h-4" />
                              <span>{conv.customer_mobile}</span>
                            </div>
                          )}
                        </div>

                        {conv.vehicle_info && conv.vehicle_info.brand && (
                          <div className="flex items-center gap-1 text-sm text-gray-700 mb-2">
                            <Car className="w-4 h-4 text-blue-600" />
                            <span className="font-medium">
                              {conv.vehicle_info.brand} {conv.vehicle_info.model}
                              {conv.vehicle_info.year && ` (${conv.vehicle_info.year})`}
                            </span>
                          </div>
                        )}

                        {conv.suggested_services && conv.suggested_services.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {conv.suggested_services.map((service, idx) => (
                              <span
                                key={idx}
                                className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded"
                              >
                                {service}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-3">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>{new Date(conv.created_at).toLocaleDateString()}</span>
                          </div>
                          <span>{conv.messages.length} messages</span>
                        </div>
                      </div>

                      <Button variant="ghost" size="sm">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Conversation Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Conversation Details</DialogTitle>
          </DialogHeader>

          {selectedConversation && (
            <div className="space-y-4">
              {/* Customer Info */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-3">Customer Information</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {selectedConversation.customer_name && (
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span>{selectedConversation.customer_name}</span>
                      </div>
                    )}
                    {selectedConversation.customer_mobile && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span>{selectedConversation.customer_mobile}</span>
                      </div>
                    )}
                    {selectedConversation.customer_email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <span>{selectedConversation.customer_email}</span>
                      </div>
                    )}
                    {selectedConversation.vehicle_info && selectedConversation.vehicle_info.brand && (
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-gray-500" />
                        <span>
                          {selectedConversation.vehicle_info.brand} {selectedConversation.vehicle_info.model}
                          {selectedConversation.vehicle_info.year && ` (${selectedConversation.vehicle_info.year})`}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Suggested Services */}
              {selectedConversation.suggested_services && selectedConversation.suggested_services.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-2">Suggested Services</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedConversation.suggested_services.map((service, idx) => (
                        <span
                          key={idx}
                          className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-sm"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Conversation Messages */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-3">Conversation</h3>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {selectedConversation.messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-3 ${
                            msg.role === 'user'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          <p className="text-sm font-medium mb-1">
                            {msg.role === 'user' ? 'Customer' : 'AI Assistant'}
                          </p>
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <p className="text-xs mt-2 opacity-70">
                            {new Date(msg.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatConversationsManager;
